import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Clock, 
  Users, 
  Gem, 
  Mountain, 
  Star, 
  ArrowRight,
  MapPin,
  DollarSign,
  Film,
  Pickaxe,
  History,
  AlertCircle
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import { museum } from "../data/mock";

const FreeMuseumPage = () => {
  const [openingHours, setOpeningHours] = useState({ closed_on_tuesdays: false });

  useEffect(() => {
    fetchOpeningHours();
  }, []);

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/opening-hours/public`);
      if (response.ok) {
        const data = await response.json();
        setOpeningHours(data);
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    }
  };
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Museum",
    "name": "Umoona Opal Mine Free Underground Museum",
    "description": "Visit our free opal mining museum in Coober Pedy. Explore authentic mining history, opal displays, underground accommodation and learn about outback life.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy", 
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    "openingHours": "Mo-Su 08:30-17:30",
    "isAccessibleForFree": true,
    "url": "https://umoonaopalmine.com.au/free-museum"
  };

  const highlights = [
    {
      icon: Film,
      title: "Story of Opal Cinema",
      description: "Award-winning 20-minute documentary presented in underground theatre on three panoramic screens"
    },
    {
      icon: Pickaxe,
      title: "Mining Heritage", 
      description: "Discover authentic mining history from the 1920s with original equipment and techniques"
    },
    {
      icon: Gem,
      title: "Fossil Collection",
      description: "View unique opalised fossils and geological specimens found in the region"
    }
  ];

  return (
    <div>
      <SEOHead
        title="Free Opal Mining Museum - Umoona Opal Mine & Museum"
        description="Visit our free opal mining museum in Coober Pedy. Explore authentic mining history, opal displays, underground accommodation and learn about outback life."
        keywords="free museum, opal museum, Coober Pedy museum, mining history, underground museum, South Australia attractions"
        canonical="/free-museum"
        schemaData={schemaData}
      />
      
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster={museum.images[0]}
          >
            <source src="/museum-hero.mp4" type="video/mp4" />
            {/* Fallback image if video fails to load */}
            <img 
              src={museum.images[0]}
              alt="Umoona Underground Museum"
              className="w-full h-full object-cover"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-4xl">
            <Badge variant="secondary" className="mb-6 bg-green-500/20 text-green-300 border border-green-400/30 px-6 py-3 text-lg">
              🎉 Completely FREE Entry • Underground Cinema
            </Badge>
            
            <h1 className="text-4xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Free Underground
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
                Museum Experience
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Beautiful museum with history of the area, its people, fossils, and opals. Great display and 
              lots of info on display with an underground cinema showing our award-winning movie.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">FREE</div>
                <div className="text-green-200 text-sm">Admission</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">8:30am</div>
                <div className="text-blue-200 text-sm">Daily</div>
                {openingHours.closed_on_tuesdays && (
                  <div className="text-red-400 text-xs font-semibold mt-2 flex items-center justify-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Closed Tuesdays
                  </div>
                )}
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">ALL AGES</div>
                <div className="text-yellow-200 text-sm">Welcome</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="mining-cta font-bold px-8 py-4 text-lg">
                <Link to="/contact">
                  Plan Your Visit
                  <MapPin className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4 text-lg backdrop-blur-sm"
              >
                <Link to="/underground-tours">
                  Add Guided Tour
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Discover */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What You'll Discover
            </h2>
            <p className="text-xl text-gray-600">
              Explore fascinating exhibits that tell the complete story of Coober Pedy's rich history
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <History className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold">History of Coober Pedy</h3>
                <p className="text-gray-600">Learn about its people and the unique underground lifestyle</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Mountain className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold">Fossil Displays</h3>
                <p className="text-gray-600">Extensive fossil displays and geological specimens</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Gem className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold">Opal Formation</h3>
                <p className="text-gray-600">Comprehensive exhibits explaining how opals are formed</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Film className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold">Underground Cinema</h3>
                <p className="text-gray-600">Award-winning 'Story of Opal' documentary</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Pickaxe className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-semibold">Mining Equipment</h3>
                <p className="text-gray-600">Original mining equipment displays</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold">Aboriginal Culture</h3>
                <p className="text-gray-600">Traditional Aboriginal cultural artifacts</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Museum Highlights */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Museum Highlights
            </h2>
          </div>

          <div className="space-y-12">
            {highlights.map((highlight, index) => (
              <div key={index} className="flex items-center">
                <Card className="w-full">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-6">
                      <div className="flex-shrink-0 bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center">
                        <highlight.icon className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{highlight.title}</h3>
                        <p className="text-gray-600 text-lg">{highlight.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visitor Information */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
            Visitor Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6 bg-green-50 border-green-200">
              <CardContent className="space-y-4 p-0">
                <h3 className="text-2xl font-bold text-green-800 flex items-center">
                  <Clock className="w-6 h-6 mr-3" />
                  Opening Hours
                </h3>
                <div className="space-y-2">
                  <p className="text-xl font-semibold text-green-700">8:30am – 5:30pm</p>
                  <p className="text-green-600">Open daily</p>
                  {openingHours.closed_on_tuesdays && (
                    <p className="text-red-600 font-semibold flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Closed on Tuesdays
                    </p>
                  )}
                  <p className="text-green-600">Free admission</p>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6 bg-blue-50 border-blue-200">
              <CardContent className="space-y-4 p-0">
                <h3 className="text-2xl font-bold text-blue-800 flex items-center">
                  <DollarSign className="w-6 h-6 mr-3" />
                  Admission
                </h3>
                <div className="space-y-2">
                  <p className="text-3xl font-bold text-blue-700">FREE ENTRY</p>
                  <p className="text-blue-600">All ages welcome</p>
                  <p className="text-blue-600">Wheelchair accessible areas available</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="p-8 bg-purple-50 border-purple-200">
              <CardContent className="p-0">
                <MapPin className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-purple-800 mb-4">Location</h3>
                <p className="text-lg text-purple-700">14 Hutchison St, Coober Pedy SA 5723</p>
                <p className="text-purple-600">Phone: (08) 8672 5288</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to Explore Underground?
              </h2>
              <p className="text-xl text-green-100 max-w-2xl mx-auto">
                Visit our free museum and discover the fascinating world beneath Coober Pedy. 
                Add a guided tour for the complete experience!
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-green-600 hover:bg-green-50 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/contact">
                  Get Directions
                  <MapPin className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-green-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/underground-tours">
                  Book Guided Tour
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeMuseumPage;